import type { Market } from "@ferrivia/api-contract";

interface DatePattern {
  order: "dmy" | "mdy";
  separator: string;
  placeholder: string;
}

export const DATE_PATTERNS: Record<Market, DatePattern> = {
  fi: { order: "dmy", separator: ".", placeholder: "pp.kk.vvvv" },
  us: { order: "mdy", separator: "/", placeholder: "mm/dd/yyyy" },
};

/** Masks whatever was typed into the market's pattern. No trailing separator
    is appended, so backspacing over one isn't immediately undone. */
export function maskDateText(raw: string, market: Market): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  const { separator } = DATE_PATTERNS[market];
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join(separator);
}

/** "" unless the text is a complete, real calendar date. */
export function dateTextToIso(text: string, market: Market): string {
  const digits = text.replace(/\D/g, "");
  if (digits.length !== 8) return "";
  const first = Number(digits.slice(0, 2));
  const second = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4));
  const [day, month] = DATE_PATTERNS[market].order === "dmy" ? [first, second] : [second, first];
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (year < 1000 || probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) {
    return "";
  }
  return `${digits.slice(4)}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function isoToDateText(iso: string, market: Market): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return "";
  const [, year, month, day] = match;
  const { order, separator } = DATE_PATTERNS[market];
  return (order === "dmy" ? [day, month, year] : [month, day, year]).join(separator);
}
